import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FilterPanel = ({ 
  filters, 
  activeFilters, 
  onFilterChange, 
  onClearFilters,
  viewMode,
  onViewModeChange 
}) => {
  const filterCategories = [
    {
      key: 'technology',
      label: 'Technology Stack',
      icon: 'Code',
      options: filters.technologies
    },
    {
      key: 'type',
      label: 'Project Type',
      icon: 'Layers',
      options: filters.types
    },
    {
      key: 'industry',
      label: 'Industry',
      icon: 'Building',
      options: filters.industries
    },
    {
      key: 'complexity',
      label: 'Complexity',
      icon: 'Zap',
      options: filters.complexity
    }
  ];

  const viewModes = [
    { key: 'grid', label: 'Grid', icon: 'Grid3X3' },
    { key: '3d', label: '3D Gallery', icon: 'Box' },
    { key: 'list', label: 'Detailed', icon: 'List' }
  ];

  return (
    <motion.div
      className="glass-morphism rounded-xl p-6 cyber-border mb-8"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-brand-cyan to-brand-purple rounded-lg flex items-center justify-center cyber-glow">
            <Icon name="Filter" size={20} color="#ffffff" />
          </div>
          <div>
            <h3 className="font-exo text-lg font-bold text-foreground">Project Filters</h3>
            <p className="text-text-secondary text-sm">Customize your exploration experience</p>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center space-x-2">
          {viewModes.map((mode) => (
            <Button
              key={mode.key}
              variant={viewMode === mode.key ? "default" : "outline"}
              size="sm"
              iconName={mode.icon}
              onClick={() => onViewModeChange(mode.key)}
              className={viewMode === mode.key ? "cyber-glow" : "cyber-border hover:cyber-border-active"}
            />
          ))}
        </div>
      </div>

      {/* Filter Categories */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {filterCategories.map((category) => (
          <div key={category.key} className="space-y-3">
            <div className="flex items-center space-x-2">
              <Icon name={category.icon} size={16} className="text-brand-cyan" />
              <h4 className="font-inter font-medium text-foreground text-sm">
                {category.label}
              </h4>
            </div>
            
            <div className="space-y-2">
              {category.options.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center space-x-2 cursor-pointer group"
                >
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={activeFilters[category.key]?.includes(option.value) || false}
                      onChange={(e) => onFilterChange(category.key, option.value, e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 rounded border-2 transition-all duration-300 ${
                      activeFilters[category.key]?.includes(option.value)
                        ? 'bg-brand-cyan border-brand-cyan cyber-glow' :'border-border group-hover:border-brand-cyan'
                    }`}>
                      {activeFilters[category.key]?.includes(option.value) && (
                        <Icon name="Check" size={12} className="text-background" />
                      )}
                    </div>
                  </div>
                  <span className="text-text-secondary text-sm group-hover:text-foreground transition-colors duration-300">
                    {option.label}
                    <span className="text-text-tertiary ml-1">({option.count})</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Active Filters & Clear */}
      {Object.values(activeFilters).some(filters => filters.length > 0) && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {Object.entries(activeFilters).map(([category, values]) =>
                values.map((value) => (
                  <motion.div
                    key={`${category}-${value}`}
                    className="flex items-center space-x-2 px-3 py-1 bg-brand-cyan/20 text-brand-cyan rounded-full text-xs cyber-border"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                  >
                    <span>{value}</span>
                    <button
                      onClick={() => onFilterChange(category, value, false)}
                      className="hover:text-foreground transition-colors duration-300"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </motion.div>
                ))
              )}
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              iconPosition="left"
              onClick={onClearFilters}
              className="text-text-secondary hover:text-foreground"
            >
              Clear All
            </Button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default FilterPanel;